import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { Form, Button } from "reactstrap";
import cx from "classnames";

import {
  CREATE_COLLECTION_SUCCESS,
  CREATE_COLLECTION_FAILED,
  UPDATE_COLLECTION_SUCCESS,
  UPDATE_COLLECTION_FAILED,
} from "../../../store/actions/actionTypes";
import { createCollection, updateCollection } from "../../../store/actions/documentActions";
import {
  convertNonAsciiToClosestAsciiEquivalent as convertToAscii,
  preventFormSubmitOnEnter as preventSubmit,
} from "../../../_helpers/utils";
import { Input } from "../../ui/input";
import { Icon } from "../../ui/icon";
import { ProcessLoader } from "../../ui/loader/processLoader";
import { FlashAlert } from "../../ui/flashAlert";

import global from "../../../scss/dhp.scss";
import AddToCollectionModal from "../../Document/modal/addToCollectionModal";

let style = Object.assign({}, global);

const CreateCollectionModal = ({ data, _helpers, toggle }) => {
  const dispatch = useDispatch();
  const alertData = {
    show: false,
    type: "",
  };
  const processEndTypes = [
    CREATE_COLLECTION_SUCCESS,
    CREATE_COLLECTION_FAILED,
    UPDATE_COLLECTION_SUCCESS,
    UPDATE_COLLECTION_FAILED,
  ];

  const form = {
    collection_name: {
      value: data?.name || "",
      validation: {
        required: true,
      },
      valid: false,
    },
  };

  const isRenameMode = !!data?.name;

  const [formData, setFormData] = useState(form);
  const [formValid, setFormValid] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [alert, setAlert] = useState(alertData);
  const [disable, setDisable] = useState(false);
  const [charCount, setCharCount] = useState((data?.name || "")?.length);
  const [openedViaAddCollection, setOpenedViaCollection] = useState(false);

  const { type } = useSelector(state => state?.document);

  const toggleAlert = () => setAlert({ ...alert, show: !alert.show });

  const checkValidity = (value, rules) => {
    let isValid = true;
    if (rules.required) isValid = value.trim() !== "" && isValid;
    if (rules.not_equals) isValid = value.trim() !== rules.not_equals && isValid;
    // minimum length must be 3
    if (value.trim().length < 3) {
      isValid = false;
    }
    // must start with an alphabet (a-z or A-Z) or number (0-9)
    if (!/^[A-Za-z0-9]/.test(value.trim())) {
      isValid = false;
    }

    return isValid;
  };

  const updateForm = (e, field) => {
    if (e.target.value.length > 30) return;
    let updatedForm = { ...formData };
    setCharCount(e.target.value.trim().length);
    updatedForm[field].value = convertToAscii(e.target.value);
    updatedForm[field].valid = checkValidity(updatedForm[field].value, updatedForm[field].validation);
    setFormData(updatedForm);
    setFormValid(Object.keys(formData).every(field => formData[field].valid));
  };

  const handleSubmit = e => {
    if (formValid) {
      e.preventDefault();
      setIsMounted(true);
      let payload = { name: formData.collection_name.value };
      setDisable(true);
      if (isRenameMode) {
        dispatch(updateCollection(data?._id, payload));
      } else {
        dispatch(createCollection(payload));
      }
    }
  };

  // To Redirect to Add Collection Modal
  const handleRedirectToModal = () => {
    _helpers.modal.setComponent({
      name: AddToCollectionModal,
    });
  };

  const handleClick = () => {
    if (_helpers?.modal?.toggle) {
      _helpers?.modal?.toggle()
      return;
    };
    if (toggle) toggle();
  };

  useEffect(() => {
    if (isMounted && type) {
      const successType = isRenameMode ? UPDATE_COLLECTION_SUCCESS : CREATE_COLLECTION_SUCCESS;
      const failedType = isRenameMode ? UPDATE_COLLECTION_FAILED : CREATE_COLLECTION_FAILED;

      if (type === successType) {
        setDisable(false);
        setIsMounted(false);
        if (isRenameMode) {
          if (_helpers?.additionalInfo?.setName) {
            _helpers?.additionalInfo?.setName(formData.collection_name.value);
            _helpers?.modal?.toggle();
            return;
          }
          if (_helpers.gridView?.fetch?.setPayload) {
            _helpers.gridView.fetch.setPayload({ ..._helpers.gridView.fetch.payload, page: 1 });
            return;
          }
          if (_helpers.fetch?.setPayload) {
            _helpers.fetch.setPayload({ ..._helpers.fetch.payload, page: 1 });
            return;
          }
        } else if (openedViaAddCollection){
          sessionStorage.setItem("fromCreateNewModal", "true");
          handleRedirectToModal();
        }else {
          _helpers?.modal?.toggle();
          _helpers.fetch.setPayload({ ..._helpers.fetch.payload, page: 1 });
        }
      }
      if (type === failedType) {
        setDisable(false);
        setIsMounted(false);
        setAlert({ ...alert, show: true, type: "error" });
      }
    }
  }, [type, isMounted]);

  // To manage if from Add Collection Modal
  useEffect(() => {
    if (sessionStorage.getItem('fromAddCollectionModal') === 'true') {
      setOpenedViaCollection(true);
    }
    sessionStorage.removeItem('fromAddCollectionModal');
  }, []);

  return (
    <React.Fragment>
      <h4 className={cx(style["fw-7"], style["mb-4"])}>
        {openedViaAddCollection && (
          <span className={style["cursor-pointer"]} onClick={handleRedirectToModal}>
            <Icon icon="ui-arrow-left" />
          </span>
        )}
        {isRenameMode ? "Rename Collection" : "Create Collection"}
      </h4>
      <Link to="#" className={cx(style["cross-modal"], style["rounded"])} onClick={handleClick}>
        <Icon icon="ui-close" />
      </Link>

      <Form className={style["mt-1"]} onKeyDown={e => preventSubmit(e)} onSubmit={e => handleSubmit(e)}>
        <Input
          cssModule={style}
          returnType="formGroup"
          type="text"
          label="Collection Name"
          required={true}
          value={formData.collection_name.value}
          onChange={e => updateForm(e, "collection_name")}
          disabled={disable}
          placeholder="Example: Client Name, Campaign Name, etc."
          maxLength={30}
        />
        <div className={cx(style["d-flex"], style["justify-content-end"])}>{`${charCount}/30`}</div>

        <div className={cx(style["align-items-center"], style["d-flex"])}>
          <Button
            type="submit"
            className={style["mt-2"]}
            color="primary"
            cssModule={style}
            disabled={
              !formValid ||
              disable ||
              (isRenameMode &&
                formData.collection_name.value.trim().toLowerCase() === (data?.name || "").trim().toLowerCase())
            }>
            Save
          </Button>

          <ProcessLoader isOpen={disable && !processEndTypes.includes(type)} />
        </div>
      </Form>

      <FlashAlert isOpen={alert.show} toggle={toggleAlert} type={alert.type} wrapperClass="mt-3" />
    </React.Fragment>
  );
};

CreateCollectionModal.propTypes = {
  data: PropTypes.object,
  _helpers: PropTypes.object,
};

export default CreateCollectionModal;
